import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-center flex flex-col items-center gap-4 mb-11" }
const _hoisted_2 = {
  key: 0,
  class: "flex flex-col md:flex-row gap-2.5 items-center justify-center mb-5"
}
const _hoisted_3 = { class: "flex justify-center flex-wrap max-sm:max-w-[220px] max-sm:mx-auto gap-2.5" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "max-w-[300px] mx-auto" }
const _hoisted_6 = { class: "flex justify-center w-full mb-3" }
const _hoisted_7 = { class: "text-center" }
const _hoisted_8 = { class: "text-lg leading-lg font-bold" }

import Checkboxes from '../form/Checkboxes.vue';
import TimeSelect from '../form/TimeSelect.vue';
import Select from '../form/Select.vue';
import Toggle from '../form/Toggle.vue';
import Badge from '../base/Badge.vue';
import Popup from '../layout/Popup.vue';
import PopupTextarea from '../layout/PopupTextarea.vue';
import IconWarning from '../icons/IconWarning.vue';
import useGetTimezones from '~src/admin/composables/useGetTimezones';
import { computed, inject, ref, watch, watchEffect } from 'vue';
import { AdvancedScheduleType, AppConfigType, OptionType, ScheduleType } from '~src/admin/types';
import { useRouter } from 'vue-router';
import Message from '../base/Message.vue';
import Button from '../base/Button.vue';
import IconPlus from '../icons/IconPlus.vue';
import { useGettext } from 'vue3-gettext';
import { useTokensStore } from '~src/admin/stores/useTokensStore';
import DateRangeSelect from '../form/DateRangeSelect.vue';

// Translations

export default /*@__PURE__*/_defineComponent({
  __name: 'BaseSchedule',
  props: {
    schedule: {},
    isPreview: { type: Boolean, default: false }
  },
  emits: ["updateSchedule", "articlesLeftUpdate"],
  setup(__props: any, { emit: __emit }) {

const { $gettext } = useGettext();

// Get plugin config
const config: AppConfigType = inject('pluginConfig');

// Props


// Emits
const emit = __emit;

// Router
const router = useRouter();

// Popup control
const upgradePopup = ref<InstanceType<typeof Popup>>();

// Get current token count
const { usedTokens, totalTokens, refreshTokens } = useTokensStore();

// Weekday options
const weekdayOptions = ref<OptionType[]>([
    { label: $gettext('Monday'), value: '1' },
    { label: $gettext('Tuesday'), value: '2' },
    { label: $gettext('Wednesday'), value: '3' },
    { label: $gettext('Thursday'), value: '4' },
    { label: $gettext('Friday'), value: '5' },
    { label: $gettext('Saturday'), value: '6' },
    { label: $gettext('Sunday'), value: '0' },
]);

// Account type
const isAccountFree = inject<boolean>('isAccountFree');

// Account expired
const isAccountExpired = inject<boolean>('isAccountExpired');

// Get all time zones for select
const timeZones = useGetTimezones();
const userTimeZone = config.wordpress.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

// Store values from inputs
const selectedTime = ref(__props.schedule.time || '10:00');
const selectedDays = ref<string | string[]>(null);
const selectedTimeZone = ref<string>(userTimeZone);
const activityDateEnabled = ref<boolean>(__props.schedule.activity_date_enabled ?? false);
const selectedActivityDate = ref<string[]>(__props.schedule.activity_date ?? []);

// Set selected days default
if (isAccountFree && __props.schedule.days) {
    selectedDays.value = __props.schedule.days?.[0] ?? '1';
} else if (!isAccountFree && __props.schedule.days) {
    selectedDays.value = __props.schedule.days;
}

// Schedule type
const isAdvancedSchedule = ref(__props.schedule.advSchedule ? true : false);

// Store all times for all days
const advancedTimes = ref<AdvancedScheduleType>(
    __props.schedule.advSchedule ?? {
        '1': [],
        '2': [],
        '3': [],
        '4': [],
        '5': [],
        '6': [],
        '0': [],
    }
);

// Calculate usage percentage
const recentUsedArticles = usedTokens;
const totalArticles = totalTokens;

// Get initial used articles
const initialUsedArticles = ref(0);

const calculateInitialUsedArticles = () => {
    if (isAdvancedSchedule.value && advancedTimes.value) {
        // Advanced schedule
        initialUsedArticles.value = Object.values(advancedTimes.value).reduce((sum, arr) => sum + arr.length, 0);
    } else if (!isAdvancedSchedule.value) {
        // Basic schedule
        initialUsedArticles.value = selectedDays.value.length;
    }
};
calculateInitialUsedArticles();

// Keyword popups
const keywordPopup = ref<InstanceType<typeof PopupTextarea>>();

// Set the current keyword and its index for editing
const articleKeywords = ref<string[][][]>(__props.schedule.articles_keywords ?? []);
const currentKeyword = ref<string>('');
const currentKeywordDayIndex = ref<number>(0);
const currentKeywordTimeIndex = ref<number>(0);

// Set keyword and index
const openKeywordPopup = (keyword: string[], dayIndex: number, timeIndex?: number) => {
    // Set the current keyword and indexes
    currentKeyword.value = keyword?.join(',') ?? '';
    currentKeywordDayIndex.value = dayIndex;
    if (timeIndex !== undefined) {
        currentKeywordTimeIndex.value = timeIndex;
    }

    // Open the popup
    keywordPopup.value.openPopup();
};

// Update selected keyword
const updateKeyword = () => {
    if (__props.schedule.advSchedule) {
        articleKeywords.value[currentKeywordDayIndex.value][currentKeywordTimeIndex.value] =
            currentKeyword.value.split(',');
    } else {
        articleKeywords.value[currentKeywordDayIndex.value] = [currentKeyword.value.split(',')];
    }

    // Close the popup
    keywordPopup.value.closePopup();
};

// Update initial used articles on refresh
watch(
    () => refreshTokens.value,
    () => {
        calculateInitialUsedArticles();
    }
);

const usedArticles = computed(() => {
    // Sum existing articles and the potential new ones
    let currentSum = 0;

    if (isAdvancedSchedule.value && advancedTimes.value) {
        // Advanced schedule
        currentSum = Object.values(advancedTimes.value).reduce((sum, arr) => sum + arr.length, 0);
    } else if (!isAdvancedSchedule.value) {
        // Basic schedule
        currentSum = selectedDays.value.length;
    }

    // Calculate the sum
    let sum = recentUsedArticles.value + currentSum - initialUsedArticles.value;

    // If the sum is less than inital, don't change it
    if (sum < usedTokens.value) {
        sum = usedTokens.value;
    }

    return sum;
});

// Calculate current article usage
const articlesPercentage = computed(() => {
    return Math.round((usedArticles.value / totalArticles.value) * 100);
});

// Calculate articles left
const articlesLeft = computed(() => {
    let articlesLeft = totalArticles.value - usedArticles.value;

    // Prevent negative values
    if (articlesLeft < 0) {
        articlesLeft = 0;
    }

    return articlesLeft;
});

// Show correct color
const messageColor = ref('success');

watchEffect(() => {
    if (articlesPercentage.value < 50) {
        messageColor.value = 'success';
    } else if (articlesPercentage.value >= 50 && articlesPercentage.value < 75) {
        messageColor.value = 'warning';
    } else {
        messageColor.value = 'danger';
    }
});

// Remove time select
const removeTimeSelect = (day: string, index: number) => {
    // Remove the time select
    advancedTimes.value[day].splice(index, 1);

    // Remove the keyword for the selected time select
    articleKeywords.value[Number(day)][index] = [];

    // If the time select has no values deselect it
    if (advancedTimes.value[day].length === 0 && Array.isArray(selectedDays.value)) {
        const dayIndex = selectedDays.value.findIndex((d) => d === day);
        selectedDays.value.splice(dayIndex, 1);
    }
};

watch(selectedDays, (newSelectedDays, oldSelectedDays) => {
    if (!Array.isArray(oldSelectedDays)) return;

    // Get deselected day
    const removed = oldSelectedDays.filter((x) => !newSelectedDays.includes(x))[0];

    // Remove the keyword for the selected day
    if (removed) {
        articleKeywords.value[removed] = [];
    }

    // Remove all dates when deselecting a whole day
    if (!isAdvancedSchedule.value) return;

    // Remove all time selects
    if (removed && advancedTimes.value?.[removed]) {
        advancedTimes.value[removed] = [];
    }

    if (!Array.isArray(newSelectedDays)) return;

    // Check for new selected day
    const added = newSelectedDays.filter((x) => !oldSelectedDays.includes(x))[0];

    // Add a default hour to a day
    if (added && advancedTimes.value?.[added]) {
        advancedTimes.value[added].push('10:00');
        articleKeywords.value[added][0] = [];
    }
});

// Check if there are no empty days when changing the schedule type
watch(isAdvancedSchedule, () => {
    if (!isAdvancedSchedule.value) return;

    for (const day in advancedTimes.value) {
        // Check if day is selected and add defualt time if it's empty
        if (selectedDays.value.includes(day) && advancedTimes.value[day].length === 0) {
            advancedTimes.value[day].push('10:00');
        }
    }
});

// Emit the new schedule on each change
watch(
    [
        selectedTime,
        selectedDays,
        selectedTimeZone,
        isAdvancedSchedule,
        articleKeywords,
        activityDateEnabled,
        selectedActivityDate,
    ],
    () => {
        // Generate new weekdays based on the type
        const newWeekDays = Array.isArray(selectedDays.value) ? selectedDays.value : selectedDays.value.split('');

        const newSchedule: ScheduleType = {
            ...__props.schedule,
            time: selectedTime.value,
            time_zone: selectedTimeZone.value,
            days: newWeekDays,
            advSchedule: isAdvancedSchedule.value ? advancedTimes.value : null,
            articles_keywords: articleKeywords.value,
            activity_date_enabled: activityDateEnabled.value,
            activity_date: selectedActivityDate.value,
        };

        emit('updateSchedule', newSchedule);
    },
    {
        // Emit on mounted in case no values are changed
        immediate: true,
    }
);

// Add a watcher for articlesLeft to emit its value when it changes
watch(
    usedArticles,
    () => {
		const newArticlesLeft = totalArticles.value - usedArticles.value;
        emit('articlesLeftUpdate', newArticlesLeft);
    },
    {
        immediate: true, // Emit on mount
    }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(Message, {
        message: `${articlesLeft.value} ${_unref($gettext)('articles out of')} ${_unref(totalArticles)} ${_unref($gettext)(
                'left to be scheduled for the week.'
            )}`,
        type: messageColor.value,
        size: "large"
      }, null, 8, ["message", "type"]),
      _createVNode(Message, {
        message: 
                _unref($gettext)(
                    'To generate an article for today, please schedule it at least 3 hours from the current time.'
                )
            ,
        type: "info",
        size: "large",
        "show-icon": true
      }, null, 8, ["message"])
    ]),
    (_ctx.isPreview || !_unref(isAccountFree) || !_unref(isAccountExpired))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", {
            class: "cursor-pointer",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(isAccountFree) ? upgradePopup.value.openPopup() : null))
          }, [
            _createVNode(Toggle, {
              class: _normalizeClass((_unref(isAccountFree) || _unref(isAccountExpired)) && 'pointer-events-none'),
              "inactive-text": _unref($gettext)('Basic schedule'),
              "active-text": _unref($gettext)('Advanced schedule'),
              modelValue: isAdvancedSchedule.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isAdvancedSchedule).value = $event))
            }, null, 8, ["class", "inactive-text", "active-text", "modelValue"])
          ]),
          (_unref(isAccountFree))
            ? (_openBlock(), _createBlock(Badge, {
                key: 0,
                class: "max-md:self-end",
                type: "warning"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref($gettext)('PRO feature')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weekdayOptions.value, (option, dayIndex) => {
        return (_openBlock(), _createElementBlock("div", {
          class: "max-sm:w-full sm:max-w-[140px] sm:flex-1",
          key: dayIndex
        }, [
          _createVNode(Checkboxes, {
            options: [option],
            name: `${_ctx.schedule.id}-weekday-${dayIndex}`,
            type: "border",
            size: "large",
            selection: _unref(isAccountFree) ? 'single' : 'multiple',
            modelValue: selectedDays.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedDays).value = $event)),
            "global-disabled": ((_unref(isAccountFree) && _ctx.isPreview) || _unref(isAccountExpired)) || articlesLeft.value <= 0
          }, null, 8, ["options", "name", "selection", "modelValue", "global-disabled"]),
          (!isAdvancedSchedule.value && _ctx.isPreview && _ctx.schedule.days.includes(option.value))
            ? (_openBlock(), _createBlock(Button, {
                key: 0,
                class: "mt-1.5 w-full",
                type: "secondary",
                text: _unref($gettext)('Edit keywords'),
                size: "small",
                "has-background": false,
                onClick: _withModifiers(($event: any) => (openKeywordPopup(articleKeywords.value[(dayIndex + 1) % 7][0], (dayIndex + 1) % 7)), ["prevent"])
              }, null, 8, ["text", "onClick"]))
            : _createCommentVNode("", true),
          (isAdvancedSchedule.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(advancedTimes.value[option.value], (_, timeIndex) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "relative mt-1.5 flex flex-col gap-1.5",
                    key: timeIndex
                  }, [
                    _createVNode(TimeSelect, {
                      size: "small",
                      modelValue: advancedTimes.value[option.value][timeIndex],
                      "onUpdate:modelValue": ($event: any) => ((advancedTimes.value[option.value][timeIndex]) = $event),
                      "add-remove": true,
                      onRemoveTimeSelect: ($event: any) => (removeTimeSelect(option.value, timeIndex))
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "onRemoveTimeSelect"]),
                    (isAdvancedSchedule.value && _ctx.isPreview)
                      ? (_openBlock(), _createBlock(Button, {
                          key: 0,
                          class: "mt-0.5 w-full",
                          type: "secondary",
                          text: _unref($gettext)('Edit keywords'),
                          size: "small",
                          "has-background": false,
                          onClick: _withModifiers(($event: any) => (
                            openKeywordPopup(
                                articleKeywords.value[(dayIndex + 1) % 7][timeIndex],
                                (dayIndex + 1) % 7,
                                timeIndex
                            )
                        ), ["prevent"])
                        }, null, 8, ["text", "onClick"]))
                      : _createCommentVNode("", true)
                  ]))
                }), 128)),
                _createVNode(Button, {
                  class: "w-full mt-1.5 bg-white",
                  type: "secondary",
                  text: _unref($gettext)('Add time'),
                  size: "small",
                  "has-background": false,
                  "icon-position": 'left',
                  disabled: !selectedDays.value.includes(option.value)|| articlesLeft.value <= 0,
                  onClick: _withModifiers(
                        () => {
                            articleKeywords.value[option.value].push([]);
                            advancedTimes.value[option.value].push('10:00');
                        }
                    , ["prevent"])
                }, {
                  default: _withCtx(() => [
                    _createVNode(IconPlus)
                  ]),
                  _: 2
                }, 1032, ["text", "disabled", "onClick"])
              ]))
            : _createCommentVNode("", true)
        ]))
      }), 128))
    ]),
    _createElementVNode("div", {
      class: _normalizeClass([
            'mx-auto',
            (!_ctx.isPreview || !_unref(isAccountFree)) && 'mt-8 max-w-[300px]',
            _ctx.isPreview && 'mt-5 flex flex-col md:flex-row gap-3 md:gap-5 md:max-w-[620px]',
        ])
    }, [
      (!isAdvancedSchedule.value)
        ? (_openBlock(), _createBlock(TimeSelect, {
            key: 0,
            class: "mb-2.5 min-w-[120px]",
            disabled: (_unref(isAccountFree) && _ctx.isPreview) || _unref(isAccountExpired),
            modelValue: selectedTime.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedTime).value = $event))
          }, null, 8, ["disabled", "modelValue"]))
        : _createCommentVNode("", true),
      _createVNode(Select, {
        class: _normalizeClass([(!_ctx.isPreview || !_unref(isAccountFree)) && 'mb-8']),
        options: _unref(timeZones),
        disabled: true,
        modelValue: selectedTimeZone.value,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedTimeZone).value = $event))
      }, null, 8, ["class", "options", "modelValue"])
    ], 2),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(Toggle, {
          class: _normalizeClass((_ctx.isPreview || _unref(isAccountFree)) && 'pointer-events-none'),
          "inactive-text": _unref($gettext)('Disable'),
          "active-text": _unref($gettext)('Enable'),
          modelValue: activityDateEnabled.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((activityDateEnabled).value = $event)),
          disabled: (_unref(isAccountFree) && _ctx.isPreview) || _unref(isAccountExpired)
        }, null, 8, ["class", "inactive-text", "active-text", "modelValue", "disabled"])
      ]),
      _createVNode(DateRangeSelect, {
        class: _normalizeClass([(_ctx.isPreview || _unref(isAccountFree) || !activityDateEnabled.value) && 'pointer-events-none', "mb-8"]),
        modelValue: selectedActivityDate.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedActivityDate).value = $event)),
        disabled: _ctx.isPreview || _unref(isAccountFree) || !activityDateEnabled.value || _unref(isAccountExpired)
      }, null, 8, ["class", "modelValue", "disabled"])
    ]),
    _createVNode(Popup, {
      ref_key: "upgradePopup",
      ref: upgradePopup,
      "cancel-button-text": _unref($gettext)('Maybe later'),
      "confirm-button-text": _unref($gettext)('Upgrade to paid plan'),
      onPopupConfirm: _cache[7] || (_cache[7] = ($event: any) => (_unref(router).push('/upgrade')))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_7, [
          _createVNode(IconWarning, { class: "mb-4 mx-auto" }),
          _createElementVNode("h2", _hoisted_8, _toDisplayString(_unref($gettext)('Upgrade needed')), 1),
          _createElementVNode("p", null, [
            _createTextVNode(_toDisplayString(_unref($gettext)('Advanced scheduling is available only on a paid plan.')) + " ", 1),
            _cache[9] || (_cache[9] = _createElementVNode("br", null, null, -1)),
            _createTextVNode(" " + _toDisplayString(_unref($gettext)('Upgrade account to use this feature.')), 1)
          ])
        ])
      ]),
      _: 1
    }, 8, ["cancel-button-text", "confirm-button-text"]),
    (_ctx.isPreview)
      ? (_openBlock(), _createBlock(PopupTextarea, {
          key: 1,
          ref_key: "keywordPopup",
          ref: keywordPopup,
          "cancel-button-text": _unref($gettext)('Cancel'),
          "confirm-button-text": _unref($gettext)('Submit'),
          heading: _unref($gettext)('Edit keywords'),
          placeholder: _unref($gettext)('Use comma or enter for separating keywords'),
          onPopupConfirm: updateKeyword,
          modelValue: currentKeyword.value,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((currentKeyword).value = $event))
        }, null, 8, ["cancel-button-text", "confirm-button-text", "heading", "placeholder", "modelValue"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})